import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useHasPermission } from '@/auth/auth-store';
import { useJournalRegisters } from '@/hooks/use-fiscal-journal';
import {
  OBJECT_TREE_ERROR,
  OBJECT_TREE_ERROR_KEY,
  SNAPSHOT_ID_PATTERN,
  objectTreeFailure,
  useCopyPrro,
  useCopyTaxObject,
  useCopyTaxOffice,
  useObjectTreeRead,
  useObjectTreeSigner,
  useReadObjectTree,
  type CodeMatch,
  type CopyPrroBody,
  type CopyTaxObjectBody,
  type ObjectTree,
  type ObjectTreeIssue,
  type ObjectTreeObject,
  type ObjectTreePrro,
} from '@/hooks/use-fiscal-object-tree';
import {
  REGISTRATION_STATUS_LABEL,
  REGISTRATION_STATUS_LABEL_KEY,
  useFiscalReferenceCodes,
  useLocationTaxObject,
  useTerminalPrro,
  type LocationTaxObject,
  type ReferenceCode,
  type RegistrationStatus,
  type TerminalPrro,
} from '@/hooks/use-fiscal-registration';
import { useFiscalSigningKeys, type SigningKey } from '@/hooks/use-fiscal-signing-keys';
import { useLocations } from '@/hooks/use-locations';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';
import { EmptyPanel, FiscalCabinetSection } from './fiscal-cabinet-section';
import { Banner } from './fiscal-form-fields';
import { CodeOptions } from './fiscal-record-panels';
import { KatottgPicker } from './fiscal-reference-pickers';
import { signingKeyErrorMessage } from './fiscal-signing-keys';

/**
 * PFC-19 — Fiscal cabinet → Object tree: what the tax service holds for the
 * taxpayer of a signing key, next to our records, with an explicit copy per
 * item. Nothing is copied unless the operator asks for that one item (never a
 * silent sync); unmatched items are normal and simply say "not linked".
 *
 * The last read stays in the URL (`?read=`), so a refresh shows it again for
 * as long as the server keeps it (30 minutes).
 */

type T = (key: string, fallback: string) => string;

const SIGNING_KEYS_PATH = '/fiscal-cabinet/signing-keys';

const KATOTTG_PATTERN = /^UA\d{17}$/;
const DIGITS_PATTERN = /^\d+$/;

/** The time, refreshed every `ms` (a read's 30-minute validity needs no finer grain). */
function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

function hhmm(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function day(iso: string | null): string {
  return iso === null ? '—' : new Date(iso).toLocaleDateString();
}

/** Whether the key's signing session is open right now. */
function sessionOpen(key: SigningKey): boolean {
  const { unlocked, expiresAt } = key.session;
  return unlocked && (expiresAt === null || new Date(expiresAt).getTime() > Date.now());
}

/** The operator's text for a refusal: ours first, then the signer's own codes. */
function failureMessage(err: unknown, t: T): string {
  const { code } = objectTreeFailure(err);
  const key = code === null ? undefined : OBJECT_TREE_ERROR_KEY[code];
  const fallback = code === null ? undefined : OBJECT_TREE_ERROR[code];
  if (key !== undefined && fallback !== undefined) return t(key, fallback);
  return signingKeyErrorMessage(err, t);
}

function codeLabel<C extends number | string>(
  codes: readonly ReferenceCode<C>[] | undefined,
  code: C | null | undefined,
): string {
  if (code === null || code === undefined) return '—';
  const name = codes?.find((entry) => entry.code === code)?.name;
  return name === undefined ? String(code) : `${code} — ${name}`;
}

function statusLabel(status: RegistrationStatus | null | undefined, t: T): string {
  if (status === null || status === undefined) return '—';
  return t(REGISTRATION_STATUS_LABEL_KEY[status], REGISTRATION_STATUS_LABEL[status]);
}

function Dot({ tone, label }: { tone: 'green' | 'grey'; label: string }) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        'inline-block h-2 w-2 shrink-0 rounded-full',
        tone === 'green' ? 'bg-emerald-500' : 'bg-muted-foreground/40',
      )}
    />
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-md border border-border bg-card" aria-label={title}>
      <header className="border-b border-border px-4 py-2 text-sm font-medium">{title}</header>
      {children}
    </section>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}

function SyncedNote({ at }: { at: string | null }) {
  const t = useT('admin_ui');
  if (at === null) return null;
  return (
    <div className="text-xs text-muted-foreground">
      {t('fiscalCabinet.objectTree.syncedAt', 'copied from the tax service on {date}').replace(
        '{date}',
        day(at),
      )}
    </div>
  );
}

/** A refusal of a copy, with the fields an incomplete copy lacks. */
function CopyError({ error }: { error: { message: string; issues: ObjectTreeIssue[] } | null }) {
  if (error === null) return null;
  return (
    <div role="alert">
      <Banner>
        {error.message}
        {error.issues.length > 0 && (
          <span className="mt-1 block">
            {error.issues.map((issue) => (
              <span key={issue.path} className="block">
                <span className="font-mono">{issue.path}</span>: {issue.message}
              </span>
            ))}
          </span>
        )}
      </Banner>
    </div>
  );
}

function useCopyError() {
  const t = useT('admin_ui');
  const [error, setError] = useState<{ message: string; issues: ObjectTreeIssue[] } | null>(null);
  return {
    error,
    clear: () => setError(null),
    fail: (err: unknown) =>
      setError({ message: failureMessage(err, t), issues: objectTreeFailure(err).issues }),
  };
}

/** One line of a copy's diff: ours → what the copy writes. */
function DiffRow({ label, ours, theirs }: { label: string; ours: ReactNode; theirs: ReactNode }) {
  const changed = typeof ours === 'string' && typeof theirs === 'string' && ours !== theirs;
  return (
    <TR>
      <TD className="text-xs text-muted-foreground">{label}</TD>
      <TD className="text-xs">{ours}</TD>
      <TD className={cn('text-xs', changed && 'font-medium')}>{theirs}</TD>
    </TR>
  );
}

function DiffTable({ children }: { children: ReactNode }) {
  const t = useT('admin_ui');
  return (
    <Table>
      <THead>
        <TR>
          <TH className="w-48" />
          <TH>{t('fiscalCabinet.objectTree.diff.ours', 'In Back Office now')}</TH>
          <TH>{t('fiscalCabinet.objectTree.diff.after', 'After the copy')}</TH>
        </TR>
      </THead>
      <TBody>{children}</TBody>
    </Table>
  );
}

// ------------------------------------------------------------------
// Taxpayer
// ------------------------------------------------------------------

function TaxpayerSection({
  tree,
  canWrite,
  expired,
}: {
  tree: ObjectTree;
  canWrite: boolean;
  expired: boolean;
}) {
  const t = useT('admin_ui');
  const copy = useCopyTaxOffice();
  const failure = useCopyError();
  const [confirming, setConfirming] = useState(false);
  const { registration, legalEntity, payer } = tree;
  const dpsOffice =
    registration.taxOfficeCode === null
      ? '—'
      : `${registration.taxOfficeCode} ${registration.taxOfficeName ?? ''}`.trim();
  const ourOffice = `${legalEntity.taxOfficeCode} ${legalEntity.taxOfficeName}`.trim();
  // DPS giving no office is never "the same" as ours.
  const same = registration.same && registration.taxOfficeCode !== null;
  const canCopy =
    canWrite &&
    !registration.same &&
    registration.taxOfficeCode !== null &&
    registration.taxOfficeKnown;

  async function onConfirm() {
    failure.clear();
    try {
      await copy.mutateAsync(tree.snapshotId);
      setConfirming(false);
    } catch (err) {
      failure.fail(err);
    }
  }

  return (
    <Section title={t('fiscalCabinet.objectTree.taxpayer', 'Taxpayer')}>
      <div className="space-y-3 p-4 text-sm">
        <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[12rem_1fr]">
          <dt className="text-muted-foreground">
            {t('fiscalCabinet.objectTree.payerName', 'Name at the tax service')}
          </dt>
          <dd>{payer.name ?? '—'}</dd>
          <dt className="text-muted-foreground">
            {t('fiscalCabinet.objectTree.payerTin', 'Tax ID')}
          </dt>
          <dd className="font-mono">{payer.tin}</dd>
          <dt className="text-muted-foreground">
            {t('fiscalCabinet.objectTree.ourEntity', 'Legal entity in Back Office')}
          </dt>
          <dd>{legalEntity.name}</dd>
        </dl>

        <Table>
          <THead>
            <TR>
              <TH className="w-48" />
              <TH>{t('fiscalCabinet.objectTree.atDps', 'At the tax service')}</TH>
              <TH>{t('fiscalCabinet.objectTree.inBackOffice', 'In Back Office')}</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD className="text-xs text-muted-foreground">
                {t('fiscalCabinet.objectTree.mainTaxOffice', 'Main tax office')}
              </TD>
              <TD className="text-xs" data-testid="tax-office-dps">
                {dpsOffice}
                {registration.registeredAt !== null && (
                  <div className="text-muted-foreground">
                    {t('fiscalCabinet.objectTree.registeredOn', 'registered on {date}').replace(
                      '{date}',
                      day(registration.registeredAt),
                    )}
                  </div>
                )}
              </TD>
              <TD className="text-xs" data-testid="tax-office-ours">
                <span className="inline-flex items-center gap-2">
                  <Dot
                    tone={same ? 'green' : 'grey'}
                    label={
                      same
                        ? t('fiscalCabinet.objectTree.same', 'The same')
                        : registration.taxOfficeCode === null
                          ? t(
                              'fiscalCabinet.objectTree.noDpsOffice',
                              'The tax service gives no main office',
                            )
                          : t('fiscalCabinet.objectTree.differs', 'Differs')
                    }
                  />
                  {ourOffice}
                </span>
                <SyncedNote at={legalEntity.dpsSyncedAt} />
              </TD>
            </TR>
          </TBody>
        </Table>

        {!registration.taxOfficeKnown && registration.taxOfficeCode !== null && (
          <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
            {t(
              'fiscalCabinet.objectTree.taxOfficeUnknown',
              'Our directory of tax offices does not list this office, so it cannot be copied until the directory is updated.',
            )}
          </p>
        )}

        {canCopy && !confirming && (
          <Button
            variant="outline"
            size="sm"
            disabled={expired}
            onClick={() => setConfirming(true)}
          >
            {t('fiscalCabinet.objectTree.copyTaxOffice', 'Copy tax office')}
          </Button>
        )}
        {canCopy && confirming && (
          <div className="space-y-2 rounded-md border border-border p-3" data-testid="copy-office">
            <p className="text-xs">
              {t(
                'fiscalCabinet.objectTree.copyTaxOfficeConfirm',
                'The legal entity {entity} will be filed with this office:',
              ).replace('{entity}', legalEntity.name)}
            </p>
            <p className="text-xs">
              <span className="text-muted-foreground line-through">{ourOffice}</span>
              {' → '}
              <span className="font-medium">{dpsOffice}</span>
            </p>
            <CopyError error={failure.error} />
            <div className="flex gap-2">
              <Button size="sm" onClick={onConfirm} disabled={expired || copy.isPending}>
                {t('fiscalCabinet.objectTree.confirmCopy', 'Copy')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setConfirming(false);
                  failure.clear();
                }}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

// ------------------------------------------------------------------
// Taxation objects
// ------------------------------------------------------------------

/** A state / rights code is asked for unless DPS used the form's own wording. */
function pickHint(match: CodeMatch, words: string | null, t: T): string {
  if (match === 'stem' && words !== null) {
    return t(
      'fiscalCabinet.objectTree.stemHint',
      'Read from the tax service’s wording «{words}» — check it.',
    ).replace('{words}', words);
  }
  return words === null
    ? t('fiscalCabinet.objectTree.notGiven', 'The tax service does not give this.')
    : t('fiscalCabinet.objectTree.dpsWords', 'The tax service says: {words}').replace(
        '{words}',
        words,
      );
}

/** The picker's starting value: our reading of DPS's words, else the store's own code. */
function startCode(match: CodeMatch, dpsCode: number | null, ours: number | undefined): string {
  if (match === 'stem' && dpsCode !== null) return String(dpsCode);
  return ours === undefined ? '' : String(ours);
}

function ObjectCopyPanel({
  tree,
  item,
  expired,
  onDone,
}: {
  tree: ObjectTree;
  item: ObjectTreeObject;
  expired: boolean;
  onDone: () => void;
}) {
  const t = useT('admin_ui');
  const codes = useFiscalReferenceCodes();
  const locations = useLocations({ status: ['active'] });
  const copy = useCopyTaxObject();
  const failure = useCopyError();
  const [locationId, setLocationId] = useState(item.match?.location.id ?? '');
  const view = useLocationTaxObject(locationId, locationId !== '');
  const { dps, proposed } = item;

  // `undefined` while unknown; `null` when the store has no tax object yet.
  // The matched store's record in the read stands in if its own view fails.
  const current: LocationTaxObject | null | undefined =
    locationId === ''
      ? undefined
      : view.data !== undefined
        ? view.data.taxObject
        : view.isError && item.match !== null && item.match.location.id === locationId
          ? item.match.taxObject
          : undefined;

  const [stateCode, setStateCode] = useState('');
  const [rightsCode, setRightsCode] = useState('');
  const [settlement, setSettlement] = useState(item.settlement ?? '');
  const [katottg, setKatottg] = useState('');

  // Each store the operator picks starts the pickers afresh, once its record
  // is known — even when the new store holds the same code as the last one.
  const loadedFor = current === undefined ? null : locationId;
  useEffect(() => {
    if (loadedFor === null) return;
    setStateCode(startCode(dps.stateMatch, dps.stateCode, current?.stateCode));
    setRightsCode(startCode(dps.rightsMatch, dps.rightsCode, current?.rightsCode));
    // Only a change of store (or its record arriving) restarts them; `current`
    // is read at that moment on purpose.
  }, [loadedFor]);

  // `exact`: DPS's code, kept by the copy. `stem`: ours to check. `none`: ours to give.
  const pickState = dps.stateMatch !== 'exact';
  const pickRights = dps.rightsMatch !== 'exact';
  const needsSettlement = current === null;
  const needsKatottg =
    dps.katottg === null && current !== undefined && (current === null || current.katottg === '');
  const katottgInvalid = needsKatottg && !KATOTTG_PATTERN.test(katottg.trim());
  // A copy never moves a store to another taxpayer (the server refuses it too).
  const otherEntity =
    current !== undefined && current !== null && current.legalEntityId !== tree.legalEntity.id;
  const missing =
    (pickState && stateCode === '') ||
    (pickRights && rightsCode === '') ||
    (needsSettlement && settlement.trim() === '') ||
    katottgInvalid;

  const stores = locations.data?.items ?? [];
  const storeOptions =
    item.match !== null && !stores.some((s) => s.id === item.match?.location.id)
      ? [item.match.location, ...stores]
      : stores;

  async function onConfirm() {
    failure.clear();
    const body: CopyTaxObjectBody = {
      locationId,
      ...(pickState ? { stateCode: Number(stateCode) } : {}),
      ...(pickRights ? { rightsCode: Number(rightsCode) } : {}),
      ...(needsSettlement ? { address: { settlement: settlement.trim() } } : {}),
      ...(needsKatottg ? { katottg: katottg.trim() } : {}),
    };
    try {
      await copy.mutateAsync({ snapshotId: tree.snapshotId, toCode: dps.toCode, body });
      onDone();
    } catch (err) {
      failure.fail(err);
    }
  }

  const objectTypeName = item.objectTypeName ?? dps.typeName;
  const ours = (value: string | null | undefined) =>
    current === undefined ? '…' : value === null || value === undefined ? '—' : value;

  return (
    <div className="space-y-3 p-3" data-testid="copy-object">
      <div className="max-w-md space-y-1">
        <Label htmlFor={`copy-object-store-${dps.toCode}`}>
          {t('fiscalCabinet.objectTree.store', 'Store')}
        </Label>
        <Select
          id={`copy-object-store-${dps.toCode}`}
          value={locationId}
          onChange={(e) => {
            setLocationId(e.target.value);
            failure.clear();
          }}
        >
          <option value="">{t('fiscalCabinet.objectTree.chooseStore', 'Choose a store…')}</option>
          {storeOptions.map((store) => (
            <option key={store.id} value={store.id}>
              {store.name} ({store.code})
            </option>
          ))}
        </Select>
        {locations.isError && (
          <p className="text-xs text-muted-foreground">
            {t(
              'fiscalCabinet.objectTree.storesUnavailable',
              'The list of stores could not be loaded.',
            )}
          </p>
        )}
      </div>

      {locationId !== '' && (
        <>
          {otherEntity && (
            <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
              {t(
                'fiscalCabinet.objectTree.error.DPS_OBJECT_OTHER_TAXPAYER',
                'This store is registered for another legal entity. A copy does not move a store to another taxpayer; change its legal entity on the store first.',
              )}
            </p>
          )}
          <DiffTable>
            <DiffRow
              label={t(
                'fiscalCabinet.taxObject.field.dpsObjectCode',
                'Object code (type + own number)',
              )}
              ours={ours(current?.dpsObjectCode)}
              theirs={proposed.dpsObjectCode}
            />
            <DiffRow
              label={t('fiscalCabinet.taxObject.field.objectType', 'Object type')}
              ours={
                current === undefined
                  ? '…'
                  : codeLabel(codes.data?.objectTypes, current?.objectTypeCode)
              }
              theirs={
                objectTypeName === null
                  ? String(proposed.objectTypeCode)
                  : `${proposed.objectTypeCode} — ${objectTypeName}`
              }
            />
            <DiffRow
              label={t('fiscalCabinet.taxObject.field.katottg', 'KATOTTG code')}
              ours={ours(current?.katottg)}
              theirs={
                needsKatottg ? (
                  <div className="space-y-2">
                    <Input
                      aria-label={t('fiscalCabinet.taxObject.field.katottg', 'KATOTTG code')}
                      value={katottg}
                      onChange={(e) => setKatottg(e.target.value)}
                      aria-invalid={katottgInvalid && katottg !== '' ? true : undefined}
                      placeholder="UA…"
                      className="h-9 font-mono"
                    />
                    {katottgInvalid && katottg !== '' && (
                      <p className="text-xs text-destructive">
                        {t(
                          'fiscalCabinet.objectTree.katottgInvalid',
                          'A KATOTTG code is UA followed by 17 digits.',
                        )}
                      </p>
                    )}
                    <KatottgPicker
                      code={katottg}
                      onPick={(unit) => {
                        setKatottg(unit.code);
                        if (needsSettlement && unit.address.settlement !== null) {
                          setSettlement(unit.address.settlement);
                        }
                      }}
                    />
                  </div>
                ) : (
                  (proposed.katottg ?? ours(current?.katottg))
                )
              }
            />
            <DiffRow
              label={t('fiscalCabinet.taxObject.field.state', 'Object state')}
              ours={
                current === undefined
                  ? '…'
                  : codeLabel(codes.data?.objectStates, current?.stateCode)
              }
              theirs={
                pickState ? (
                  <CodePicker
                    id={`copy-object-state-${dps.toCode}`}
                    label={t('fiscalCabinet.taxObject.field.state', 'Object state')}
                    hint={pickHint(dps.stateMatch, dps.state, t)}
                    codes={codes.data?.objectStates}
                    value={stateCode}
                    onChange={setStateCode}
                  />
                ) : (
                  codeLabel(codes.data?.objectStates, proposed.stateCode)
                )
              }
            />
            <DiffRow
              label={t('fiscalCabinet.taxObject.field.rights', 'Right to the object')}
              ours={
                current === undefined
                  ? '…'
                  : codeLabel(codes.data?.objectRights, current?.rightsCode)
              }
              theirs={
                pickRights ? (
                  <CodePicker
                    id={`copy-object-rights-${dps.toCode}`}
                    label={t('fiscalCabinet.taxObject.field.rights', 'Right to the object')}
                    hint={pickHint(dps.rightsMatch, dps.rights, t)}
                    codes={codes.data?.objectRights}
                    value={rightsCode}
                    onChange={setRightsCode}
                  />
                ) : (
                  codeLabel(codes.data?.objectRights, proposed.rightsCode)
                )
              }
            />
            <DiffRow
              label={t('fiscalCabinet.objectTree.registration', 'Registration')}
              ours={current === undefined ? '…' : statusLabel(current?.dpsRegistrationStatus, t)}
              theirs={statusLabel(proposed.dpsRegistrationStatus, t)}
            />
            <DiffRow
              label={t('fiscalCabinet.objectTree.registeredAt', 'Registered on')}
              ours={current === undefined ? '…' : day(current?.dpsRegisteredAt ?? null)}
              theirs={day(proposed.dpsRegisteredAt)}
            />
            <DiffRow
              label={t('fiscalCabinet.taxObject.field.objectName', 'Object name')}
              ours={ours(current?.objectName)}
              theirs={current === undefined ? '…' : (current?.objectName ?? dps.name ?? '—')}
            />
            <DiffRow
              label={t('fiscalCabinet.address.settlement', 'Settlement')}
              ours={ours(current?.address.settlement)}
              theirs={
                needsSettlement ? (
                  <Input
                    aria-label={t('fiscalCabinet.address.settlement', 'Settlement')}
                    value={settlement}
                    onChange={(e) => setSettlement(e.target.value)}
                    className="h-9"
                  />
                ) : (
                  ours(current?.address.settlement)
                )
              }
            />
          </DiffTable>
          {current !== undefined && current !== null && <SyncedNote at={current.dpsSyncedAt} />}
          <p className="text-xs text-muted-foreground">
            {t(
              'fiscalCabinet.objectTree.copyObjectHelp',
              'The tax service’s code, type, KATOTTG, state, rights and registration replace ours; everything else on the store’s record is kept. Nothing is sent to the tax service.',
            )}
          </p>
        </>
      )}

      <CopyError error={failure.error} />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={onConfirm}
          disabled={
            expired ||
            locationId === '' ||
            current === undefined ||
            otherEntity ||
            missing ||
            copy.isPending
          }
        >
          {t('fiscalCabinet.objectTree.confirmCopy', 'Copy')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );
}

/** A required pick of a code the tax service does not give (or that we only read from its words). */
function CodePicker<C extends number | string>({
  id,
  label,
  hint,
  codes,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint: string | null;
  codes: ReferenceCode<C>[] | undefined;
  value: string;
  onChange: (value: string) => void;
}) {
  const t = useT('admin_ui');
  return (
    <div className="space-y-1">
      <Select id={id} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('fiscalCabinet.objectTree.choose', 'Choose…')}</option>
        <CodeOptions codes={codes ?? []} current={null} />
      </Select>
      {hint !== null && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ObjectsSection({
  tree,
  canWrite,
  expired,
}: {
  tree: ObjectTree;
  canWrite: boolean;
  expired: boolean;
}) {
  const t = useT('admin_ui');
  const [open, setOpen] = useState<string | null>(null);
  const columns = canWrite ? 10 : 9;
  return (
    <Section title={t('fiscalCabinet.objectTree.objects', 'Taxation objects')}>
      {tree.objects.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          {t(
            'fiscalCabinet.objectTree.noObjects',
            'The tax service lists no taxation objects for this taxpayer.',
          )}
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH className="w-6" />
              <TH>{t('fiscalCabinet.taxObject.field.objectType', 'Object type')}</TH>
              <TH>{t('fiscalCabinet.objectTree.col.toCode', 'Object code')}</TH>
              <TH>{t('fiscalCabinet.taxObject.field.state', 'Object state')}</TH>
              <TH>{t('fiscalCabinet.taxObject.field.rights', 'Right to the object')}</TH>
              <TH>{t('fiscalCabinet.objectTree.registeredAt', 'Registered on')}</TH>
              <TH>{t('fiscalCabinet.objectTree.deregisteredAt', 'Deregistered on')}</TH>
              <TH>{t('fiscalCabinet.address.settlement', 'Settlement')}</TH>
              <TH>{t('fiscalCabinet.objectTree.col.ourStore', 'Our store')}</TH>
              {canWrite && <TH />}
            </TR>
          </THead>
          <TBody>
            {tree.objects.map((item) => {
              const registered = item.dps.deregisteredAt === null;
              return (
                <Fragment key={item.dps.toCode}>
                  <TR data-testid="object-row">
                    <TD>
                      <Dot
                        tone={registered ? 'green' : 'grey'}
                        label={statusLabel(registered ? 'registered' : 'deregistered', t)}
                      />
                    </TD>
                    <TD className="text-xs">{item.objectTypeName ?? item.dps.typeName ?? '—'}</TD>
                    <TD className="font-mono text-xs">{item.dps.toCode}</TD>
                    <TD className="text-xs">{item.dps.state ?? '—'}</TD>
                    <TD className="text-xs">{item.dps.rights ?? '—'}</TD>
                    <TD className="whitespace-nowrap text-xs">{day(item.dps.registeredAt)}</TD>
                    <TD className="whitespace-nowrap text-xs">{day(item.dps.deregisteredAt)}</TD>
                    <TD className="text-xs">{item.settlement ?? '—'}</TD>
                    <TD className="text-xs">
                      {item.match === null ? (
                        <Muted>{t('fiscalCabinet.objectTree.notLinked', 'not linked')}</Muted>
                      ) : (
                        <>
                          {item.match.location.name}
                          <SyncedNote at={item.match.taxObject.dpsSyncedAt} />
                        </>
                      )}
                    </TD>
                    {canWrite && (
                      <TD className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={expired}
                          onClick={() => setOpen(open === item.dps.toCode ? null : item.dps.toCode)}
                        >
                          {t('fiscalCabinet.objectTree.copyToStore', 'Copy to store…')}
                        </Button>
                      </TD>
                    )}
                  </TR>
                  {canWrite && open === item.dps.toCode && (
                    <TR>
                      <TD colSpan={columns} className="bg-muted/30">
                        <ObjectCopyPanel
                          tree={tree}
                          item={item}
                          expired={expired}
                          onDone={() => setOpen(null)}
                        />
                      </TD>
                    </TR>
                  )}
                </Fragment>
              );
            })}
          </TBody>
        </Table>
      )}
    </Section>
  );
}

// ------------------------------------------------------------------
// PRROs
// ------------------------------------------------------------------

function PrroCopyPanel({
  tree,
  item,
  expired,
  onDone,
}: {
  tree: ObjectTree;
  item: ObjectTreePrro;
  expired: boolean;
  onDone: () => void;
}) {
  const t = useT('admin_ui');
  const codes = useFiscalReferenceCodes();
  const registers = useJournalRegisters(true);
  const copy = useCopyPrro();
  const failure = useCopyError();
  const [terminalId, setTerminalId] = useState(item.match?.terminal.id ?? '');
  const view = useTerminalPrro(terminalId, terminalId !== '');
  const [prroTypeCode, setPrroTypeCode] = useState('');
  const [offlineModeCode, setOfflineModeCode] = useState('');
  const [prroName, setPrroName] = useState('');
  const [localNumber, setLocalNumber] = useState('');
  const { dps, proposed } = item;

  // `undefined` while unknown; `null` when the till has no PRRO record yet.
  const current: TerminalPrro | null | undefined =
    terminalId === ''
      ? undefined
      : view.data !== undefined
        ? view.data.prro
        : view.isError && item.match !== null && item.match.terminal.id === terminalId
          ? item.match.prro
          : undefined;

  const needsCodes = current === null;
  // What DPS leaves out and the till does not have yet (used only when DPS gives none).
  const needsName =
    dps.name === null && current !== undefined && (current === null || current.prroName === '');
  const needsLocalNumber =
    dps.localNumber === null &&
    current !== undefined &&
    (current === null || current.localNumber === '');
  const localNumberInvalid = needsLocalNumber && !DIGITS_PATTERN.test(localNumber.trim());
  const missing =
    (needsCodes && (prroTypeCode === '' || offlineModeCode === '')) ||
    (needsName && prroName.trim() === '') ||
    localNumberInvalid;
  // The server refuses this too (DPS_PRRO_OTHER_TAXPAYER); say it before the click.
  const tillEntity = view.data?.legalEntity ?? null;
  const otherEntity = tillEntity !== null && tillEntity.id !== tree.legalEntity.id;

  // A revoked till takes no new PRRO data (PFC-25), even when this PRRO was
  // matched to it before revocation. The row remains visible above.
  const tills = (registers.data ?? []).filter((r) => r.terminalStatus !== 'revoked');
  const selectedRevoked =
    registers.data?.some((r) => r.terminalId === terminalId && r.terminalStatus === 'revoked') ??
    false;
  useEffect(() => {
    if (selectedRevoked) setTerminalId('');
  }, [selectedRevoked]);
  const tillOptions =
    item.match !== null &&
    !registers.data?.some(
      (r) => r.terminalId === item.match?.terminal.id && r.terminalStatus === 'revoked',
    ) &&
    !tills.some((r) => r.terminalId === item.match?.terminal.id)
      ? [
          {
            terminalId: item.match.terminal.id,
            terminalName: item.match.terminal.displayName,
            terminalCode: item.match.terminal.code,
            location: item.match.location,
          },
          ...tills,
        ]
      : tills;

  async function onConfirm() {
    failure.clear();
    try {
      await copy.mutateAsync({
        snapshotId: tree.snapshotId,
        fiscalNumber: dps.fiscalNumber,
        body: {
          terminalId,
          ...(needsCodes ? { prroTypeCode, offlineModeCode } : {}),
          ...(needsName ? { prroName: prroName.trim() } : {}),
          ...(needsLocalNumber ? { localNumber: localNumber.trim() } : {}),
        } satisfies CopyPrroBody,
      });
      onDone();
    } catch (err) {
      failure.fail(err);
    }
  }

  const ours = (value: string | null | undefined) =>
    current === undefined ? '…' : value === null || value === undefined ? '—' : value;

  return (
    <div className="space-y-3 p-3" data-testid="copy-prro">
      <div className="max-w-md space-y-1">
        <Label htmlFor={`copy-prro-till-${dps.fiscalNumber}`}>
          {t('fiscalCabinet.objectTree.till', 'Till')}
        </Label>
        <Select
          id={`copy-prro-till-${dps.fiscalNumber}`}
          value={terminalId}
          onChange={(e) => {
            setTerminalId(e.target.value);
            failure.clear();
          }}
        >
          <option value="">{t('fiscalCabinet.objectTree.chooseTill', 'Choose a till…')}</option>
          {tillOptions.map((till) => (
            <option key={till.terminalId} value={till.terminalId}>
              {till.terminalName} ({till.terminalCode}) · {till.location?.name ?? '—'}
            </option>
          ))}
        </Select>
      </div>

      {terminalId !== '' && (
        <>
          {otherEntity && (
            <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
              {t(
                'fiscalCabinet.objectTree.error.DPS_PRRO_OTHER_TAXPAYER',
                "This till's store is registered for another legal entity, so this PRRO cannot be copied to it.",
              )}
            </p>
          )}
          <DiffTable>
            <DiffRow
              label={t('fiscalCabinet.prro.field.fiscalNumber', 'Fiscal number')}
              ours={ours(current?.fiscalNumber)}
              theirs={proposed.fiscalNumber}
            />
            <DiffRow
              label={t('fiscalCabinet.prro.field.localNumber', 'Local number')}
              ours={ours(current?.localNumber)}
              theirs={
                needsLocalNumber ? (
                  <div className="space-y-1">
                    <Input
                      aria-label={t('fiscalCabinet.prro.field.localNumber', 'Local number')}
                      value={localNumber}
                      onChange={(e) => setLocalNumber(e.target.value)}
                      inputMode="numeric"
                      className="h-9 font-mono"
                    />
                    {localNumberInvalid && localNumber !== '' && (
                      <p className="text-xs text-destructive">
                        {t('fiscalCabinet.objectTree.digitsOnly', 'Digits only.')}
                      </p>
                    )}
                  </div>
                ) : (
                  (proposed.localNumber ?? ours(current?.localNumber))
                )
              }
            />
            <DiffRow
              label={t('fiscalCabinet.prro.field.name', 'PRRO name')}
              ours={ours(current?.prroName)}
              theirs={
                needsName ? (
                  <Input
                    aria-label={t('fiscalCabinet.prro.field.name', 'PRRO name')}
                    value={prroName}
                    onChange={(e) => setPrroName(e.target.value)}
                    className="h-9"
                  />
                ) : (
                  (proposed.prroName ?? ours(current?.prroName))
                )
              }
            />
            <DiffRow
              label={t('fiscalCabinet.objectTree.registration', 'Registration')}
              ours={current === undefined ? '…' : statusLabel(current?.registrationStatus, t)}
              theirs={
                current === undefined
                  ? '…'
                  : statusLabel(
                      proposed.registrationStatus ?? current?.registrationStatus ?? 'unregistered',
                      t,
                    )
              }
            />
            <DiffRow
              label={t('fiscalCabinet.objectTree.registeredAt', 'Registered on')}
              ours={current === undefined ? '…' : day(current?.registeredAt ?? null)}
              theirs={day(proposed.registeredAt ?? current?.registeredAt ?? null)}
            />
            <DiffRow
              label={t('fiscalCabinet.prro.field.type', 'PRRO type')}
              ours={
                current === undefined
                  ? '…'
                  : codeLabel(codes.data?.prroTypes, current?.prroTypeCode)
              }
              theirs={
                needsCodes ? (
                  <CodePicker
                    id={`copy-prro-type-${dps.fiscalNumber}`}
                    label={t('fiscalCabinet.prro.field.type', 'PRRO type')}
                    hint={null}
                    codes={codes.data?.prroTypes}
                    value={prroTypeCode}
                    onChange={setPrroTypeCode}
                  />
                ) : current === undefined ? (
                  '…'
                ) : (
                  codeLabel(codes.data?.prroTypes, current?.prroTypeCode)
                )
              }
            />
            <DiffRow
              label={t('fiscalCabinet.prro.field.offlineMode', 'Offline mode')}
              ours={
                current === undefined
                  ? '…'
                  : codeLabel(codes.data?.prroOfflineModes, current?.offlineModeCode)
              }
              theirs={
                needsCodes ? (
                  <CodePicker
                    id={`copy-prro-offline-${dps.fiscalNumber}`}
                    label={t('fiscalCabinet.prro.field.offlineMode', 'Offline mode')}
                    hint={null}
                    codes={codes.data?.prroOfflineModes}
                    value={offlineModeCode}
                    onChange={setOfflineModeCode}
                  />
                ) : current === undefined ? (
                  '…'
                ) : (
                  codeLabel(codes.data?.prroOfflineModes, current?.offlineModeCode)
                )
              }
            />
          </DiffTable>
          {current !== undefined && current !== null && <SyncedNote at={current.dpsSyncedAt} />}
          <p className="text-xs text-muted-foreground">
            {t(
              'fiscalCabinet.objectTree.copyPrroHelp',
              'The tax service’s numbers, name and registration replace ours; the PRRO type and offline mode are not in its reply and are kept. Nothing is sent to the tax service.',
            )}
          </p>
        </>
      )}

      <CopyError error={failure.error} />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={onConfirm}
          disabled={
            expired ||
            registers.data === undefined ||
            terminalId === '' ||
            current === undefined ||
            missing ||
            otherEntity ||
            selectedRevoked ||
            copy.isPending
          }
        >
          {t('fiscalCabinet.objectTree.confirmCopy', 'Copy')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  );
}

function PrrosSection({
  tree,
  canWrite,
  expired,
}: {
  tree: ObjectTree;
  canWrite: boolean;
  expired: boolean;
}) {
  const t = useT('admin_ui');
  const [open, setOpen] = useState<string | null>(null);
  const columns = canWrite ? 9 : 8;
  return (
    <Section title={t('fiscalCabinet.objectTree.prros', 'PRROs')}>
      {tree.prros.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          {t(
            'fiscalCabinet.objectTree.noPrros',
            'The tax service lists no PRROs for this taxpayer.',
          )}
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH className="w-6" />
              <TH>{t('fiscalCabinet.prro.field.fiscalNumber', 'Fiscal number')}</TH>
              <TH>{t('fiscalCabinet.prro.field.localNumber', 'Local number')}</TH>
              <TH>{t('fiscalCabinet.prro.field.name', 'PRRO name')}</TH>
              <TH>{t('fiscalCabinet.objectTree.col.dpsStatus', 'Status at the tax service')}</TH>
              <TH>{t('fiscalCabinet.objectTree.registeredAt', 'Registered on')}</TH>
              <TH>{t('fiscalCabinet.objectTree.col.address', 'Address')}</TH>
              <TH>{t('fiscalCabinet.objectTree.col.ourTill', 'Our till')}</TH>
              {canWrite && <TH />}
            </TR>
          </THead>
          <TBody>
            {tree.prros.map((item) => {
              const { dps } = item;
              return (
                <Fragment key={dps.fiscalNumber}>
                  <TR data-testid="prro-row">
                    <TD>
                      <Dot
                        tone={dps.registrationStatus === 'registered' ? 'green' : 'grey'}
                        label={statusLabel(dps.registrationStatus, t)}
                      />
                    </TD>
                    <TD className="font-mono text-xs">{dps.fiscalNumber}</TD>
                    <TD className="font-mono text-xs">{dps.localNumber ?? '—'}</TD>
                    <TD className="text-xs">{dps.name ?? '—'}</TD>
                    <TD className="text-xs">
                      {dps.status ?? statusLabel(dps.registrationStatus, t)}
                    </TD>
                    <TD className="whitespace-nowrap text-xs">{day(dps.registeredAt)}</TD>
                    <TD className="text-xs">{dps.address ?? '—'}</TD>
                    <TD className="text-xs">
                      {item.match === null ? (
                        <Muted>{t('fiscalCabinet.objectTree.notLinked', 'not linked')}</Muted>
                      ) : (
                        <>
                          {item.match.terminal.displayName}
                          {item.match.location !== null && (
                            <Muted> · {item.match.location.name}</Muted>
                          )}
                          <SyncedNote at={item.match.prro.dpsSyncedAt} />
                        </>
                      )}
                    </TD>
                    {canWrite && (
                      <TD className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={expired}
                          onClick={() =>
                            setOpen(open === dps.fiscalNumber ? null : dps.fiscalNumber)
                          }
                        >
                          {t('fiscalCabinet.objectTree.copyToTill', 'Copy to till…')}
                        </Button>
                      </TD>
                    )}
                  </TR>
                  {canWrite && open === dps.fiscalNumber && (
                    <TR>
                      <TD colSpan={columns} className="bg-muted/30">
                        <PrroCopyPanel
                          tree={tree}
                          item={item}
                          expired={expired}
                          onDone={() => setOpen(null)}
                        />
                      </TD>
                    </TR>
                  )}
                </Fragment>
              );
            })}
          </TBody>
        </Table>
      )}
    </Section>
  );
}

// ------------------------------------------------------------------
// Page
// ------------------------------------------------------------------

function SigningKeysLink({ children }: { children: ReactNode }) {
  return (
    <Link to={SIGNING_KEYS_PATH} className="underline">
      {children}
    </Link>
  );
}

/** Why the last read failed: calm for the stub signer, a pointer for a locked key. */
function ReadFailure({ error }: { error: unknown }) {
  const t = useT('admin_ui');
  const { code } = objectTreeFailure(error);
  if (code === 'FISCAL_SIGNING_NOT_CONFIGURED') {
    return (
      <EmptyPanel>
        {t(
          'fiscalCabinet.objectTree.signingNotEnabled',
          'Reading from the tax service needs real signing, which is not enabled on this server yet.',
        )}
      </EmptyPanel>
    );
  }
  return (
    <p role="alert" className="text-sm text-destructive">
      {failureMessage(error, t)}
      {code === 'KEY_SESSION_LOCKED' && (
        <>
          {' '}
          <SigningKeysLink>
            {t('fiscalCabinet.objectTree.openSigningKeys', 'Open Signing keys')}
          </SigningKeysLink>
        </>
      )}
    </p>
  );
}

function TreeView({ tree, canWrite }: { tree: ObjectTree; canWrite: boolean }) {
  const t = useT('admin_ui');
  const now = useNow();
  // The server forgets the read then, so a copy would only be refused.
  const expired = new Date(tree.expiresAt).getTime() <= now;
  return (
    <div className="space-y-4" data-testid="object-tree">
      <p className="text-xs text-muted-foreground">
        {t('fiscalCabinet.objectTree.readAt', 'Read at {read}, valid until {until}')
          .replace('{read}', hhmm(tree.readAt))
          .replace('{until}', hhmm(tree.expiresAt))}
      </p>
      {expired && (
        <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
          {t('fiscalCabinet.objectTree.readExpired', 'This read has expired — read again.')}
        </p>
      )}
      <TaxpayerSection tree={tree} canWrite={canWrite} expired={expired} />
      <ObjectsSection tree={tree} canWrite={canWrite} expired={expired} />
      <PrrosSection tree={tree} canWrite={canWrite} expired={expired} />
    </div>
  );
}

function ObjectTreeBody({ available }: { available: boolean }) {
  const t = useT('admin_ui');
  const canSign = useHasPermission('fiscal.cabinet.sign');
  const canWrite = useHasPermission('fiscal.cabinet.write');
  const keys = useFiscalSigningKeys('current', available);
  const signer = useObjectTreeSigner(available);
  const [params, setParams] = useSearchParams();
  const requested = params.get('read');
  // Only a read id the server could have issued is used; anything else never
  // reaches a request and leaves the URL.
  const snapshotId = requested !== null && SNAPSHOT_ID_PATTERN.test(requested) ? requested : null;
  const tree = useObjectTreeRead(snapshotId);
  const read = useReadObjectTree();
  const [keyId, setKeyId] = useState<string | null>(null);
  const [readError, setReadError] = useState<unknown>(null);
  const [expired, setExpired] = useState(false);

  function setSnapshot(id: string | null) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === null) next.delete('read');
        else next.set('read', id);
        return next;
      },
      { replace: true },
    );
  }

  const malformed = requested !== null && snapshotId === null;
  useEffect(() => {
    if (malformed) setSnapshot(null);
  }, [malformed]);

  const expiredNow = tree.isError && objectTreeFailure(tree.error).code === 'DPS_READ_EXPIRED';
  // A read the server no longer keeps leaves the URL quietly.
  useEffect(() => {
    if (!expiredNow) return;
    setExpired(true);
    setSnapshot(null);
  }, [expiredNow]);

  // `null` while unknown: neither the read button nor the unlock hint is shown
  // yet. If the question itself fails, the read is offered and the server answers.
  const signing: boolean | null = signer.data?.signingConfigured ?? (signer.isError ? true : null);

  const current = keys.data?.items.filter((k) => k.current) ?? [];
  const selected = current.find((k) => k.id === (keyId ?? tree.data?.keyId)) ?? current[0] ?? null;
  const unlocked = selected !== null && sessionOpen(selected);

  async function onRead() {
    if (selected === null) return;
    setReadError(null);
    setExpired(false);
    try {
      const answer = await read.mutateAsync(selected.id);
      setSnapshot(answer.snapshotId);
    } catch (err) {
      setReadError(err);
    }
  }

  let keyPanel: ReactNode;
  if (signing === false) {
    // On the stub signer no key can be unlocked; a kept read below still shows.
    keyPanel = (
      <EmptyPanel>
        {t(
          'fiscalCabinet.objectTree.signingNotEnabled',
          'Reading from the tax service needs real signing, which is not enabled on this server yet.',
        )}
      </EmptyPanel>
    );
  } else if (keys.isLoading) {
    keyPanel = <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  } else if (keys.isError || keys.data === undefined) {
    keyPanel = (
      <p className="text-sm text-destructive">
        {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
      </p>
    );
  } else if (selected === null) {
    keyPanel = (
      <EmptyPanel>
        {t(
          'fiscalCabinet.objectTree.noKeys',
          'Reading from the tax service needs a signing key. Upload one for the legal entity first.',
        )}{' '}
        <SigningKeysLink>
          {t('fiscalCabinet.objectTree.openSigningKeys', 'Open Signing keys')}
        </SigningKeysLink>
      </EmptyPanel>
    );
  } else {
    keyPanel = (
      <>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-72 space-y-1">
            <Label htmlFor="object-tree-key">
              {t('fiscalCabinet.objectTree.key', 'Signing key')}
            </Label>
            <Select
              id="object-tree-key"
              value={selected.id}
              onChange={(e) => {
                setKeyId(e.target.value);
                setReadError(null);
              }}
            >
              {current.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.legalEntity.name} · {k.legalEntity.tin}
                </option>
              ))}
            </Select>
          </div>
          {signing === true && (
            <span className="inline-flex h-10 items-center gap-2 text-xs" data-testid="key-session">
              <Dot
                tone={unlocked ? 'green' : 'grey'}
                label={
                  unlocked
                    ? t('fiscalCabinet.objectTree.unlocked', 'Unlocked')
                    : t('fiscalCabinet.objectTree.locked', 'Locked')
                }
              />
              {unlocked && selected.session.expiresAt !== null
                ? t('fiscalCabinet.objectTree.unlockedUntil', 'unlocked until {time}').replace(
                    '{time}',
                    hhmm(selected.session.expiresAt),
                  )
                : unlocked
                  ? t('fiscalCabinet.objectTree.unlocked', 'Unlocked')
                  : t('fiscalCabinet.objectTree.lockedWord', 'locked')}
            </span>
          )}
          {canSign && signing === true && (
            <Button onClick={onRead} disabled={!unlocked || read.isPending}>
              {read.isPending
                ? t('fiscalCabinet.objectTree.reading', 'Reading…')
                : t('fiscalCabinet.objectTree.read', 'Read from the tax service')}
            </Button>
          )}
        </div>
        {!canSign && (
          <p className="text-xs text-muted-foreground">
            {t(
              'fiscalCabinet.objectTree.noSignPermission',
              'Reading from the tax service needs the permission to sign with the fiscal cabinet keys.',
            )}
          </p>
        )}
        {canSign && signing === true && !unlocked && (
          <p className="text-xs text-muted-foreground" data-testid="unlock-hint">
            {t(
              'fiscalCabinet.objectTree.unlockHint',
              'The key is locked. Unlock it on the Signing keys tab to read from the tax service.',
            )}{' '}
            <SigningKeysLink>
              {t('fiscalCabinet.objectTree.openSigningKeys', 'Open Signing keys')}
            </SigningKeysLink>
          </p>
        )}
      </>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          'fiscalCabinet.objectTree.help',
          'What the tax service holds for a taxpayer — the main tax office, taxation objects and PRROs — next to our records. Reading changes nothing; copy each item you want on its own.',
        )}
      </p>
      {keyPanel}

      {readError !== null && <ReadFailure error={readError} />}
      {expired && (
        <p role="status" className="text-sm text-muted-foreground">
          {t(
            'fiscalCabinet.objectTree.error.DPS_READ_EXPIRED',
            'The read has expired; read again.',
          )}
        </p>
      )}

      {snapshotId !== null && tree.isLoading && (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      )}
      {snapshotId !== null && tree.isError && !expiredNow && (
        <p className="text-sm text-destructive">
          {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
        </p>
      )}
      {snapshotId !== null && tree.data !== undefined && (
        <TreeView tree={tree.data} canWrite={canWrite} />
      )}
    </div>
  );
}

/** PFC-19 — Fiscal cabinet → Object tree. */
export function FiscalObjectTreePage() {
  return (
    <FiscalCabinetSection>
      {(available) => <ObjectTreeBody available={available} />}
    </FiscalCabinetSection>
  );
}
